import { useCommonEntitiesStore } from "@/States/commonEntities";
import { formatNumberWithDecimalPlaces } from "@/Utils/formatFunctions";
import { sortByOrderRank } from "@/Utils/wasteStreams";
import { timezoneStore, useConfig } from "@/api/hooks/useConfig";
import { useConfigService } from "@/pages/configuration/useConfigService";
import { COLORS } from "@/pages/customer/dashboard/functions/pieChartData";

import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { uniqBy } from "lodash";
import moment from "moment-timezone";
import {
  Bar,
  BarChart,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { GetCustomerPeriodsResponse } from "server/dwhApi/customers/models.js";

import ShowAllWasteStreamsToggle from "./toggle";

export const defaultWasteStreamGroupingCutOff = 0.03;

type Props = {
  data: GetCustomerPeriodsResponse;
};

const DevelopmentInWasteTypesTenantGraph: React.FC<Props> = ({ data }) => {
  const { timezone } = timezoneStore();
  const { t } = useTranslation();
  const { config } = useConfig();
  const { terminalConfig, wasteTypeConfig } = useConfigService();
  const { wasteTypes: sanityWasteTypes } = useCommonEntitiesStore();
  const allWasteTypesInUse = data.periods.flatMap(
    (period) => period.customer.wasteTypes,
  );
  const [showAllWasteStreams, setShowAllWasteStreams] = useState(false);

  const uniqueWasteTypesCodesInUse = uniqBy(allWasteTypesInUse, "code");

  const otherTitle = t("circularityLabels:otherWasteTypes");

  // Show all waste streams if true, otherwise filter out those below 3%

  const numberBelowLimit = uniqueWasteTypesCodesInUse.filter(
    (wt) => wt.share < defaultWasteStreamGroupingCutOff,
  ).length;

  const limit = useMemo(() => {
    // If only one waste type is below the limit, show it
    if (numberBelowLimit === 1 || showAllWasteStreams) {
      return 0;
    }
    return defaultWasteStreamGroupingCutOff;
  }, [numberBelowLimit, showAllWasteStreams]);

  const barData = useMemo(() => {
    const { periods } = data;
    let actualPeriods = [];
    const nrPeriods = periods.length;
    const currentMonthData = periods[nrPeriods - 1];
    if (currentMonthData?.customer.totalOccurrences > 0) {
      actualPeriods = periods.slice(1); // remove first month to keep nr months consistent
    } else {
      actualPeriods = periods.slice(0, -1); // remove current month because it has no data
    }

    return actualPeriods.map((month) => {
      const wasteTypes = month.customer?.wasteTypes ?? [];

      const monthData = wasteTypes
        .filter((wt) =>
          uniqueWasteTypesCodesInUse.map((uw) => uw.code).includes(wt.code),
        )
        .reduce(
          (acc, wt) => {
            if (wt.share < limit) {
              acc[otherTitle] = (acc[otherTitle] ?? 0) + wt.weight.quantity;
              return acc;
            }
            acc[wt.code] = wt.weight.quantity;
            return acc;
          },
          {} as Record<string, number>,
        );
      return {
        name: moment(month.startTime).tz(timezone).format("MMM"),
        ...monthData,
      };
    });
  }, [data, limit, otherTitle, timezone, uniqueWasteTypesCodesInUse]);

  const wasteTypesInBarData = [...new Set(barData.flatMap(Object.keys))].filter(
    (key) => key !== "name",
  );

  return (
    <div className="flex h-full w-full flex-col items-end">
      {numberBelowLimit > 1 && (
        <ShowAllWasteStreamsToggle
          checked={showAllWasteStreams}
          onChange={(setChecked) => {
            setShowAllWasteStreams(setChecked);
          }}
          defaultLimit={defaultWasteStreamGroupingCutOff}
        />
      )}
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={barData}
          margin={{
            top: 20,
            right: 0,
            left: 10,
            bottom: 5,
          }}
          barCategoryGap={2}
        >
          <YAxis
            tickFormatter={(value) =>
              `${formatNumberWithDecimalPlaces(value, 0).toString()} kg`
            }
          />
          <XAxis dataKey="name" axisLine={false} tickLine={false} />
          <Tooltip
            content={(props) => (
              <div className="bg-white p-4 shadow-md">
                <ul>
                  {props?.payload
                    ?.map((pl: any, index) => (
                      <li key={index}>
                        <div
                          className="mr-2 inline-block h-3 w-3"
                          style={{ backgroundColor: pl.fill }}
                        />
                        {pl.name}:{" "}
                        <strong>
                          {formatNumberWithDecimalPlaces(pl.value, 1)} kg
                        </strong>
                      </li>
                    ))
                    .reverse()}
                </ul>
              </div>
            )}
          />
          {sortByOrderRank(wasteTypesInBarData, sanityWasteTypes || [], "id")
            .reverse()
            .map((wt, index) => {
              const configWasteType = wasteTypeConfig.find(
                (swt) => swt.wasteTypeCode === wt,
              );
              const sanityWasteType = sanityWasteTypes?.find(
                (swt) => swt.id === wt,
              );
              return (
                <Bar
                  dataKey={wt}
                  key={index}
                  name={sanityWasteType?.name ?? wt}
                  stackId="a"
                  fill={
                    wt === otherTitle
                      ? terminalConfig?.otherColor ||
                        config.otherColor ||
                        "#B2ABAB"
                      : configWasteType?.color || COLORS[index % COLORS.length]
                  }
                />
              );
            })}
          <Legend
            layout="vertical"
            wrapperStyle={{
              padding: "20px",
            }}
            align="right"
            verticalAlign="middle"
            formatter={(value) => <span className="text-black">{value}</span>}
            content={(props) => {
              const { payload } = props;
              return (
                <ul>
                  {[...(payload ?? [])].reverse().map((entry, index) => (
                    <li
                      className="mb-2 flex max-w-[120px] items-center"
                      key={`item-${index}`}
                    >
                      <span
                        className="mr-2 inline-block h-4 w-4 min-w-4"
                        style={{ backgroundColor: entry.color }}
                      />
                      <span className="text-xs text-black">{entry.value}</span>
                    </li>
                  ))}
                </ul>
              );
            }}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default DevelopmentInWasteTypesTenantGraph;
